"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from "react";
import Image from "next/image";
import { Link } from "@/components/Link";
import useFramerMotion, { useInView, useReducedMotion, LazyMotion, AnimatePresence, m, type Variants } from "@/hooks/useFramerMotion";
import { FiArrowLeft, FiArrowRight, FiPause, FiPlay } from "react-icons/fi";
import { useSwipeable } from "react-swipeable";
import { useLocale, useTranslations } from "next-intl";
import { Locale } from "@/i18n/routing";
import { items } from "@/data/Testimonials";
type CardLocale = {
  title: string;
  quote: string;
};
type Card = {
  id: number;
  name: string;
  de: CardLocale;
  en: CardLocale;
  company: string;
  photo?: string;
  link: string;
};

// Unbiased shuffle on a *copy* — never mutate the imported module array,
// and don't use sort(() => Math.random()-0.5) (non-uniform, and impure in render).
const shuffle = (arr: Card[]) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Dwell time per card: ~5 chars/word × 100ms/word (≈600 wpm) + a base so short quotes still linger.
const readingTime = (quote: string, base: number) => quote.length / 5 * 100 + base;

// Reduced motion: cross-fade in place (no full-viewport slide).
const makeSlideVariants = (reduce: boolean): Variants => ({
  center: {
    x: 0,
    opacity: 1,
    transition: reduce ? {
      duration: 0.2
    } : {
      x: {
        type: "spring",
        duration: 0.4
      },
      opacity: {
        duration: 0.5
      }
    }
  },
  enter: (direction: number) => ({
    x: reduce ? 0 : direction > 0 ? "100vw" : "-100vw",
    opacity: 0,
    transition: reduce ? {
      duration: 0.2
    } : {
      x: {
        type: "spring",
        duration: 0.8
      },
      opacity: {
        duration: 0.4
      }
    }
  }),
  leave: (direction: number) => ({
    x: reduce ? 0 : direction < 0 ? "100vw" : "-100vw",
    opacity: 0,
    transition: reduce ? {
      duration: 0.2
    } : {
      x: {
        type: "spring",
        duration: 0.8
      },
      opacity: {
        duration: 0.4
      }
    }
  })
});
const TestimonialsSwiper = t0 => {
  const $ = _c(52);
  const {
    timeout: t1
  } = t0;
  const timeout = t1 === undefined ? 10000 : t1;
  const framerMotionFeatures = useFramerMotion();
  const locale = useLocale() as Locale;
  const ref = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      amount: "some"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const isInView = useInView(ref, t2);
  const reduce = useReducedMotion();
  const t3 = !!reduce;
  let t4;
  if ($[1] !== t3) {
    t4 = makeSlideVariants(t3);
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const slideVariants = t4;
  const t = useTranslations("common");
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = shuffle(items as Card[]);
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const cards = t5;
  const numCards = cards.length;
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [interacting, setInteracting] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const paused = userPaused || interacting || !isInView;
  const card = cards[index];
  const t6 = card[locale];
  let t7;
  if ($[4] !== t6.quote || $[5] !== timeout) {
    t7 = readingTime(t6.quote, timeout);
    $[4] = t6.quote;
    $[5] = timeout;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const duration = t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = dir => {
      setDirection(dir);
      setIndex(i => (i + dir + numCards) % numCards);
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const paginate = t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => paginate(1);
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const next = t9;
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => paginate(-1);
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const prev = t10;
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        next();
      } else {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          prev();
        }
      }
    };
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const onKeyDown = t11;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      onSwipedLeft: next,
      onSwipedRight: prev,
      preventScrollOnSwipe: true,
      onTouchStartOrOnMouseDown: () => setInteracting(true),
      onTouchEndOrOnMouseUp: () => setInteracting(false)
    };
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const handlers = useSwipeable(t12);
  let t13;
  if ($[12] !== t) {
    t13 = t("testimonials");
    $[12] = t;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const t14 = !userPaused;
  let t15;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setUserPaused(_temp);
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== index || $[16] !== t14) {
    t16 = <Navigation index={index} count={numCards} setNext={next} setPrevious={prev} playing={t14} onToggle={t15} />;
    $[15] = index;
    $[16] = t14;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => next();
    t18 = () => setInteracting(true);
    t19 = () => setInteracting(false);
    t20 = e => {
      if (e.animationName === "ringSweep") {
        next();
      }
    };
    $[18] = t17;
    $[19] = t18;
    $[20] = t19;
    $[21] = t20;
  } else {
    t17 = $[18];
    t18 = $[19];
    t19 = $[20];
    t20 = $[21];
  }
  const t21 = userPaused ? "polite" : "off";
  const t22 = `card-${card.id}`;
  const t23 = `${index + 1} / ${numCards}`;
  const t24 = card[locale];
  let t25;
  if ($[22] !== card || $[23] !== duration || $[24] !== paused || $[25] !== t24.title) {
    t25 = <figcaption><ClientInfo {...card} title={t24.title} duration={duration} paused={paused} /></figcaption>;
    $[22] = card;
    $[23] = duration;
    $[24] = paused;
    $[25] = t24.title;
    $[26] = t25;
  } else {
    t25 = $[26];
  }
  const t26 = card[locale];
  let t27;
  if ($[27] !== t26.quote) {
    t27 = <blockquote className="mx-auto max-w-[41ch] hyphens-auto whitespace-pre-wrap text-pretty text-center font-sans font-light text-primary/70 ~text-xl/[1.75rem] ~mt-4/6 ~leading-[2rem]/[2.5rem]">{t26.quote}</blockquote>;
    $[27] = t26.quote;
    $[28] = t27;
  } else {
    t27 = $[28];
  }
  let t28;
  if ($[29] !== t25 || $[30] !== t27) {
    t28 = <figure>{t25}{t27}</figure>;
    $[29] = t25;
    $[30] = t27;
    $[31] = t28;
  } else {
    t28 = $[31];
  }
  let t29;
  if ($[32] !== direction || $[33] !== slideVariants || $[34] !== t22 || $[35] !== t23 || $[36] !== t28) {
    t29 = <m.div key={t22} variants={slideVariants} custom={direction} initial="enter" animate="center" exit="leave" role="group" aria-roledescription="slide" aria-label={t23}>{t28}</m.div>;
    $[32] = direction;
    $[33] = slideVariants;
    $[34] = t22;
    $[35] = t23;
    $[36] = t28;
    $[37] = t29;
  } else {
    t29 = $[37];
  }
  let t30;
  if ($[38] !== direction || $[39] !== t29) {
    t30 = <AnimatePresence mode="popLayout" initial={false} custom={direction}>{t29}</AnimatePresence>;
    $[38] = direction;
    $[39] = t29;
    $[40] = t30;
  } else {
    t30 = $[40];
  }
  let t31;
  if ($[41] !== handlers || $[42] !== t21 || $[43] !== t30) {
    t31 = <div onClick={t17} onMouseEnter={t18} onMouseLeave={t19} onAnimationEnd={t20} aria-live={t21} {...handlers}>{t30}</div>;
    $[41] = handlers;
    $[42] = t21;
    $[43] = t30;
    $[44] = t31;
  } else {
    t31 = $[44];
  }
  let t32;
  if ($[45] !== t13 || $[46] !== t16 || $[47] !== t31) {
    t32 = <div ref={ref} role="group" aria-roledescription="carousel" aria-label={t13} onKeyDown={onKeyDown} className="relative">{t16}{t31}</div>;
    $[45] = t13;
    $[46] = t16;
    $[47] = t31;
    $[48] = t32;
  } else {
    t32 = $[48];
  }
  let t33;
  if ($[49] !== framerMotionFeatures || $[50] !== t32) {
    t33 = <LazyMotion features={framerMotionFeatures}>{t32}</LazyMotion>;
    $[49] = framerMotionFeatures;
    $[50] = t32;
    $[51] = t33;
  } else {
    t33 = $[51];
  }
  return t33;
};
const ClientInfo = t0 => {
  const $ = _c(24);
  const {
    name,
    title,
    link,
    company,
    photo,
    duration,
    paused
  } = t0;
  let t1;
  if ($[0] !== duration || $[1] !== paused) {
    t1 = <Spinner duration={duration} paused={paused} />;
    $[0] = duration;
    $[1] = paused;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== name || $[4] !== photo || $[5] !== title) {
    t2 = <div className="relative size-full flex-1 overflow-hidden rounded-full">{photo ? <Image src={photo} fill={true} className="size-full bg-transparent object-cover object-top" sizes="74px" alt={`${name} - ${title}`} /> : <div className="size-full bg-primary/10" />}</div>;
    $[3] = name;
    $[4] = photo;
    $[5] = title;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className="relative flex aspect-square w-20 shrink-0 skew-y-3 flex-col rounded-full p-[0.1875rem]">{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== name) {
    t4 = <p className="text-primary ~text-sm/lg">{name}</p>;
    $[10] = name;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== company || $[13] !== link) {
    t5 = <Link className="hover:text-primary" href={link} target="_blank" rel="noopener" onClick={_temp2}>{company}</Link>;
    $[12] = company;
    $[13] = link;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5 || $[16] !== title) {
    t6 = <p className="text-primary/50 ~text-xs/sm">{title} at{" "}{t5}</p>;
    $[15] = t5;
    $[16] = title;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <div className="text-balance font-secondary lg:max-w-prose">{t4}{t6}</div>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t3 || $[22] !== t7) {
    t8 = <div className="underline-links flex w-full items-center leading-tight ~gap-2/3">{t3}{t7}</div>;
    $[21] = t3;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};

// Progress ring — pure presentation, no hooks. The sweep is a CSS animation
// (`ringSweep` in styles.css) whose length is the slide's reading time and whose
// play-state follows `paused`. Rendered fresh per slide, so it restarts on each
// card automatically. Its completion is the autoplay clock: `onAnimationEnd` on
// the slides container advances the carousel — so DON'T disable this animation
// (e.g. `motion-reduce:animate-none`) or autoplay stops with it.
const Spinner = t0 => {
  const $ = _c(4);
  const {
    duration,
    paused
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <circle cx="50" cy="50" r="48" pathLength="1" className="fill-none stroke-dark/0 stroke-[0.1875rem]" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `ringSweep ${duration}ms linear forwards`;
  const t3 = paused ? "paused" : "running";
  let t4;
  if ($[1] !== t2 || $[2] !== t3) {
    t4 = <svg width="100" height="100" viewBox="0 0 100 100" className="absolute left-0 top-0 z-10 size-20 -rotate-90">{t1}<circle cx="50" cy="50" r="48" pathLength="1" strokeDasharray="1" strokeDashoffset="1" className="fill-none stroke-[0.1875rem]" style={{
        stroke: "var(--clr-primary-dark)",
        animation: t2,
        animationPlayState: t3
      }} /></svg>;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
};
const Navigation = t0 => {
  const $ = _c(39);
  const {
    index,
    count,
    setNext,
    setPrevious,
    playing,
    onToggle
  } = t0;
  const t = useTranslations("common");
  let t1;
  if ($[0] !== playing || $[1] !== t) {
    t1 = playing ? t("pause") : t("play");
    $[0] = playing;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !playing;
  let t3;
  if ($[3] !== playing) {
    t3 = playing ? <FiPause className="-skew-y-3 fill-current stroke-0 text-dark transition-all duration-300 ~text-lg/xl group-hover/nav:text-white" /> : <FiPlay className="-skew-y-3 fill-current stroke-0 text-dark transition-all duration-300 ~text-lg/xl group-hover/nav:text-white" />;
    $[3] = playing;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onToggle || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <button type="button" title={t1} aria-pressed={t2} onClick={onToggle} className="group/nav flex aspect-square skew-y-3 items-center justify-center rounded-full border border-dark bg-white transition-colors duration-300 ~p-0.5/1 hover:bg-dark">{t3}</button>;
    $[5] = onToggle;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("previous");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== setPrevious) {
    t6 = () => setPrevious();
    $[12] = setPrevious;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FiArrowLeft className="-skew-y-3 text-dark transition-all duration-300 ~text-lg/2xl group-hover/nav:text-white" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t6) {
    t8 = <button type="button" title={t5} onClick={t6} className="group/nav flex aspect-square skew-y-3 items-center justify-center rounded-full border border-dark bg-white transition-colors duration-300 ~p-[0.6875rem]/5 hover:bg-dark">{t7}</button>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = index + 1;
  let t10;
  if ($[18] !== t9) {
    t10 = <span className="min-w-5 text-right">{t9}</span>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span>/</span>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== count) {
    t12 = <span className="min-w-5">{count}</span>;
    $[21] = count;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t12) {
    t13 = <div className="flex font-secondary font-bold ~text-sm/base">{t10}{t11}{t12}</div>;
    $[23] = t10;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t) {
    t14 = t("next");
    $[26] = t;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== setNext) {
    t15 = () => setNext();
    $[28] = setNext;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <FiArrowRight className="-skew-y-3 text-dark transition-all duration-300 ~text-lg/2xl group-hover/nav:text-white" />;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t14 || $[32] !== t15) {
    t17 = <button type="button" title={t14} onClick={t15} className="group/nav flex aspect-square skew-y-3 items-center justify-center rounded-full border border-dark bg-white transition-colors duration-300 ~p-[0.6875rem]/5 hover:bg-dark">{t16}</button>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t13 || $[35] !== t17 || $[36] !== t4 || $[37] !== t8) {
    t18 = <div className="relative z-10 flex items-center justify-start ~mb-4/6 ~gap-2/5 lg:absolute lg:right-0 lg:top-0 lg:mb-0">{t4}{t8}{t13}{t17}</div>;
    $[34] = t13;
    $[35] = t17;
    $[36] = t4;
    $[37] = t8;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  return t18;
};
export default TestimonialsSwiper;
function _temp(p) {
  return !p;
}
function _temp2(e) {
  return e.stopPropagation();
}